import React from 'react';
import X from 'wix-ui-icons-common/X';
import PrettyIcon from '../PrettyIcon';
import { node, func, string, oneOf, bool, number, oneOfType } from 'prop-types';
import styles from './ModalLayout.scss';
import classNames from 'classnames';

import { Heading, Text } from 'wix-style-react';

const inputRange = [0, 50];

const interpolate = ({ outputRange, n }) => {
  const result =
    (1 - n / (inputRange[1] - inputRange[0])) * outputRange[0] + (n / (inputRange[1] - inputRange[0])) * outputRange[1];

  const min = Math.min(outputRange[0], outputRange[1]);
  const max = Math.max(outputRange[0], outputRange[1]);
  return Math.min(Math.max(result, min), max);
};

export default class ModalLayout extends React.Component {
  state = { scrollTop: 0, smallHeader: false };

  handleScroll = (e) => {
    if (!this.props.scrollable) {
      return;
    }

    const { scrollTop } = e.target;
    this.setState({ scrollTop });
  };

  static getTitleStyle(n, paddingBottom, card) {
    const paddingVertial = interpolate({ inputRange, outputRange: [30, 18], n });
    const fontSize = interpolate({ inputRange, outputRange: [card ? 20 : 24, 16], n });
    const lineHeight = interpolate({ inputRange, outputRange: [1, 1.5], n });
    return {
      paddingTop: paddingVertial,
      paddingBottom: paddingBottom ? paddingBottom : paddingVertial,
      fontSize: fontSize,
      lineHeight: lineHeight,
    };
  }

  renderTitle() {
    const { paddingBottom, card, title } = this.props;
    const { smallHeader } = this.state;
    const titleStyle = ModalLayout.getTitleStyle(this.state.scrollTop, paddingBottom, card);

    if (card) {
      return (
        <Heading appearance="H3" className={classNames([styles.title, styles.titleCard])} dataHook="modal-layout-title">
          {title}
        </Heading>
      );
    } else if (smallHeader) {
      return (
        <Text
          size="medium"
          weight="bold"
          className={classNames([styles.title, smallHeader && styles.titleSmall])}
          dataHook="modal-layout-title"
          style={titleStyle}
        >
          {title}
        </Text>
      );
    } else {
      return (
        <Heading
          appearance="H2"
          className={classNames([styles.title, smallHeader && styles.titleSmall])}
          dataHook="modal-layout-title"
          style={titleStyle}
        >
          {title}
        </Heading>
      );
    }
  }

  render() {
    const {
      children,
      title,
      footer,
      onCloseClick,
      contentElementId,
      size,
      showCloseIcon,
      scrollable,
      contentPadding,
      card,
      prefix,
    } = this.props;

    const n = this.state.scrollTop;
    const opacity = interpolate({ inputRange, outputRange: [0, 1], n });

    const contentOverflow = this.props.contentOverflow ? { overflow: this.props.contentOverflow } : {};
    return (
      <div
        data-hook="modal-layout"
        className={classNames(styles.wrapper, {
          [styles.wrapperSmall]: size === 'small',
          [styles.wrapperLarge]: size === 'large',
          [styles.wrapperMedium]: size === 'medium',
          [styles.card]: card,
        })}
      >
        <div className={classNames(styles.innerWrapper)}>
          {prefix}
          {title && (
            <div className={styles.titleWrapper}>
              {this.renderTitle()}
              {showCloseIcon && !card && (
                <PrettyIcon className={styles.closeButton} onClick={onCloseClick} data-hook="modal-layout-close-button">
                  <X />
                </PrettyIcon>
              )}
            </div>
          )}
          <div className={styles.divider} style={{ opacity }} />
          <div
            id={contentElementId}
            className={classNames([styles.content, scrollable && styles.scrollable])}
            onScroll={this.handleScroll}
            style={{ padding: contentPadding, ...contentOverflow }}
          >
            {children}
          </div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </div>
    );
  }
}

ModalLayout.propTypes = {
  title: node,
  footer: node,
  children: node,
  onCloseClick: func,
  contentElementId: string,
  size: oneOf(['small', 'medium', 'large']),
  showCloseIcon: bool,
  scrollable: bool,
  contentPadding: oneOfType([number, string]),
  card: bool,
  contentOverflow: string,
  prefix: node,
};

ModalLayout.defaultProps = {
  showCloseIcon: true,
  scrollable: true,
};
